import { GameState } from "../enum/GameState";
import { PacketType } from "../enum/PacketType";
import { PacketValueSerialType } from "../enum/PacketValueSerialType";
import { PlayerClassType } from "../enum/PlayerClassType";
import { Input, KeyCode } from "../lib/Input";
import NetworkBehavior from "../lib/NetworkBehavior";
import { Time } from "../lib/Time";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import { PacketValue } from "../type/PacketValue";
import { clamp, clamp01, MetersToUnits, toPixels, toUnits, UnitsToMeters } from "../util";
import Color from "../util/Color";
import Listener from "../util/Listener";
import { Random } from "../util/Random";
import Asteroid from "./Asteroid";
import Explosion from "./Explosion";
import GameManager from "./GameManager";
import Fighter from "./player-classes/Fighter";
import Healer from "./player-classes/Healer";
import Scout from "./player-classes/Scout";
import Sniper from "./player-classes/Sniper";
import Tank from "./player-classes/Tank";
import PlayerClass from "./PlayerClass";
import Trail from "./Trail";

export default class Player extends NetworkBehavior {
    acceleration: Vector2 = Vector2.zero;
    velocity: Vector2 = Vector2.zero;
    trail: Trail|undefined;
    name: string = "Player";
    team: "red"|"blue"|undefined;
    class: PlayerClass|undefined;

    listeners: Listener = new Listener();

    lastPosition: Vector2 = Vector2.zero;
    isMarked = false;

    friction = 0.5;
    // topSpeed = toPixels(MetersToUnits(200));
    accelerationForce = toPixels(MetersToUnits(100));
    size = 8;

    hp = 100;
    maxHp = 100;

    energy = 100;
    maxEnergy = 100;
    energyCooldown = 0;
    energyRechargeCooldown = 0;

    isBoosting = false;
    isDisabled = false;
    timeDisabled: number|undefined;

    isDead = false;
    respawnTimer = 0;
    damageReductionModifier = 0;

    chargedShot = false;

    isCloaked = false;

    private spectatingPlayerIndex: number = 0;

    UseUnergy(amt: number) {
        this.energy -= amt;
        if (this.energy <= 0) {
            this.energy = 0;
            this.energyCooldown = 7;
        }

        this.energyRechargeCooldown = 1;
    }

    Damage(amt: number, from?: string) {
        if (this.isDead) return;
        
        const hitBy = from ? this.engine.GetGameObjectsBehaviors<Player>(Player).filter(v => v.gameObject.id === from)[0] : undefined;

        const reduction = amt * (1 - this.damageReductionModifier);
        this.hp -= Math.floor(reduction);
        this.listeners.send("damage-taken", {amount: amt, from});

        if (this.hp > this.maxHp) {
            this.hp = this.maxHp;
        }

        console.log(`took ${reduction} dmg from ${hitBy?.name ?? 'unknown'}`);

        if (this.hp <= 0) {
            this.Die();
            this.engine.sendChat(hitBy ? `${hitBy.name} killed ${this.name}` : `${this.name} died`);
        }
    }

    Heal(amt: number) {
        if (this.isDead) return;

        this.hp += amt;

        if (this.hp > this.maxHp) {
            this.hp = this.maxHp;
        }
    }

    Die() {
        this.hp = 0;
        this.isDead = true;
        this.respawnTimer = 3;

        const [, explosion] = this.engine.NewGameObjectWithScript<Explosion>(Explosion);
        explosion.transform.position = this.transform.position.clone();
        explosion.color = this.trail?.color ?? new Color(255, 255, 80);
        explosion.size = this.size * 4;
        explosion.w = 32;

        this.engine.sendCreatePacket(explosion.gameObject, {
            type: "EXPLOSION",
            values: [
                {
                    key: "color",
                    value: this.team === 'red' ? new Color(255, 80, 80) : new Color(80, 80, 255),
                    type: PacketValueSerialType.Color
                },
                {
                    key: "size",
                    value: explosion.size,
                    type: PacketValueSerialType.Byte
                },
                {
                    key: "w",
                    value: explosion.w,
                    type: PacketValueSerialType.Byte
                }
            ]
        });
    }

    SetClass(pClass: PlayerClassType) {
        switch (pClass) {
            case PlayerClassType.Fighter:
                this.class = new Fighter(this);
                break;
            case PlayerClassType.Tank:
                this.class = new Tank(this);
                break;
            case PlayerClassType.Scout:
                this.class = new Scout(this);
                break;
            case PlayerClassType.Sniper:
                this.class = new Sniper(this);
                break;
            case PlayerClassType.Healer:
                this.class = new Healer(this);
                break;
        
            default:
                break;
        }

        if (this.isOwner) {
            this.engine.sendPacket(PacketType.SetPlayerClass, packet => {
                packet.writeBytes(pClass);
            });
        }
    }

    Init(): void {
        this.trail = this.gameObject.AddScript(Trail);
        // this.SetClass(Random.int(0, 5));
    }

    Sync() {
        if (!this.isOwner || GameManager.Singleton?.getGameState() !== GameState.InGame) return;

        const deltas = this.CalculateDeltas();
        this.engine.sendUpdateObjectPacket(this.gameObject, {
            "PLAYER": deltas
        });
    }
    SyncFullState() {
        if (!this.isOwner || GameManager.Singleton?.getGameState() !== GameState.InGame) return;

        const vals: PacketValue[] = [];

        for (const key in this.lastState) {
            const lval = this.lastState[key];
            //@ts-ignore
            const val = this[key];

            if (lval.type === PacketValueSerialType.Vector2) {
                vals.push({
                    key,
                    value: val.clone(),
                    type: lval.type
                });
            } else {
                vals.push({
                    key,
                    value: val,
                    type: lval.type
                });
            }
        }

        this.engine.sendUpdateObjectPacket(this.gameObject, {
            "PLAYER": vals
        });
    }

    lastState: Record<string, {
        type: PacketValueSerialType;
        value: any;
    }> = {
        team: {
            type: PacketValueSerialType.String,
            value: ""
        },
        isDead: {
            type: PacketValueSerialType.Bool,
            value: false
        },
        hp: {
            type: PacketValueSerialType.UInt,
            value: 0
        },
        maxHp: {
            type: PacketValueSerialType.UInt,
            value: 0
        },
        size: {
            type: PacketValueSerialType.Byte,
            value: 0
        },
        isCloaked: {
            type: PacketValueSerialType.Bool,
            value: false
        },
        damageReductionModifier: {
            type: PacketValueSerialType.Float,
            value: 0
        },
        isMarked: {
            type: PacketValueSerialType.Bool,
            value: false
        },
        isDisabled: {
            type: PacketValueSerialType.Bool,
            value: false
        },
        velocity: {
            type: PacketValueSerialType.Vector2,
            value: Vector2.zero
        },
    };

    CalculateDeltas() {

        const deltas: PacketValue[] = [];

        for (const key in this.lastState) {
            const lastVal = this.lastState[key];
            //@ts-ignore
            const currentVal = this[key];

            if (lastVal.type === PacketValueSerialType.Vector2) {
                if (!lastVal.value.equals(currentVal)) {
                    deltas.push({
                        key,
                        type: lastVal.type,
                        value: currentVal.clone()
                    });
                }
            } else {
                if (lastVal !== currentVal) {
                    deltas.push({
                        key,
                        type: lastVal.type,
                        value: currentVal
                    });
                }
            }
        }

        return deltas;
    }

    ClientUpdate() {
        // this.velocity = this.transform.position.sub(this.lastPosition).scale(1/Time.deltaTime);
        // this.lastPosition = this.transform.position.clone();
        this.velocity = this.velocity.sub(this.velocity.scale(this.friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
    }

    DeathUpdate() {
        this.respawnTimer -= Time.deltaTime;

        if (this.respawnTimer <= 0 && this.team) {
            this.transform.position = GameManager.GetSpawn(this.team);
            this.velocity = Vector2.zero;
            this.acceleration = Vector2.zero;
            this.isDead = false;
            this.hp = this.maxHp;
            this.energy = this.maxEnergy;
            
            this.respawnTimer = 0;
        }
    }

    SpectateUpdate() {
        const players = this.engine.GetGameObjectsBehaviors<Player>(Player).filter(v => v.id !== this.id);
        const spectating = players[clamp(this.spectatingPlayerIndex, 0, players.length - 1)];

        if (Input.GetMouseButtonDown(0)) {
            //next player
            this.spectatingPlayerIndex = (this.spectatingPlayerIndex + 1) % players.length;
        }

        if (Input.GetMouseButtonDown(2)) {
            //prev player
            this.spectatingPlayerIndex--;
            if (this.spectatingPlayerIndex < 0) this.spectatingPlayerIndex += players.length;
        }

        this.engine.camera = Vector2.lerp(this.engine.camera, spectating.transform.position, 16 * Time.deltaTime);
        if (Vector2.distance(this.engine.camera, spectating.transform.position) < 1) this.engine.camera = spectating.transform.position.clone();
    }

    Update(): void {
        if (this.isOwner && GameManager.Singleton?.getGameState() === GameState.Lobby) {
            //lobby update
            if (!this.class) this.SetClass(Random.int(0, 5));
            //@ts-ignore
            this.SetClass(this.class.type);
            return;
        }

        if (!this.isOwner) {
            this.ClientUpdate();
        }

        if (this.isDisabled && !this.timeDisabled) {
            this.timeDisabled = Time.time;

        } else if (!this.isDisabled && this.timeDisabled) {
            this.timeDisabled = undefined;
        }

        if (this.isDisabled && this.timeDisabled && Time.time - this.timeDisabled >= 5) {
            this.isDisabled = false;
        }

        if (this.trail) this.trail.isDisabled = this.isCloaked;

        if (!this.isOwner || GameManager.Singleton?.getGameState() !== GameState.InGame) return;

        if (GameManager.Singleton?.getGameState() === GameState.InGame && !this.team) {
            this.SpectateUpdate();
            return;
        }

        if (this.isDead) {
            this.DeathUpdate();
            return;
        }

        if (this.hp <= 0) this.Die();

        this.class?.Update();
        
        const input = this.isDisabled ? Vector2.zero : new Vector2(
            Input.GetKey(KeyCode.KeyD) ? 1 : Input.GetKey(KeyCode.KeyA) ? -1 : 0,
            Input.GetKey(KeyCode.KeyS) ? 1 : Input.GetKey(KeyCode.KeyW) ? -1 : 0
        );

        if (input.squareMagnitude > 0) input.normalize();

        this.isBoosting = Input.GetKey(KeyCode.ShiftLeft) && this.energy > 0 && this.energyCooldown === 0;

        this.acceleration = input.scale(this.accelerationForce * (this.isBoosting ? 2 : 1));

        const vecToMouse = this.engine.mouseWorldPosition.sub(this.transform.position);
        this.transform.rotation = vecToMouse.normalized;

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(this.friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));

        if (this.isBoosting) {
            this.energy -= 2 * Time.deltaTime;
            this.energyRechargeCooldown = 1;

            if (this.energy <= 0) {
                this.energy = 0;
                this.energyCooldown = 7;
            }
        }

        if (this.energyCooldown > 0) {
            this.energyCooldown -= Time.deltaTime;

            if (this.energyCooldown <= 0) this.energyCooldown = 0;
        }

        if (this.energyRechargeCooldown > 0) {
            this.energyRechargeCooldown -= Time.deltaTime;

            if (this.energyRechargeCooldown <= 0) this.energyRechargeCooldown = 0;
        } else if (this.energy < this.maxEnergy) {
            this.energy += 10 * Time.deltaTime;

            if (this.energy >= this.maxEnergy) this.energy = this.maxEnergy;
        }

        for (const asteroid of this.engine.GetGameObjectsBehaviors<Asteroid>(Asteroid)) {
            if (Vector2.distance(this.transform.position, asteroid.transform.position) > this.size + 100) continue;

            if (Physics.CircleShapeCollision({
                position: this.transform.position,
                radius: this.size
            }, asteroid.pointVectors.map(v => asteroid.transform.position.add(v)))) {
                this.Die();
                this.engine.sendChat(`${this.name} faceplanted into an asteroid`);
            }
        }

        for (const player of this.engine.GetGameObjectsBehaviors<Player>(Player).filter(v => !v.isDead && v.team && v.team !== this.team)) {
            if (player.Equals(this)) continue;

            const dist = Vector2.distance(player.transform.position, this.transform.position);

            if (dist <= player.size + this.size) {
                if (this.velocity.magnitude + player.velocity.magnitude >= 200) {
                    this.Die();
                    this.engine.sendChat(`${this.name} crashed into ${player.name}`);
                    this.engine.sendUpdatePacket(player, "PLAYER", [
                        {
                            key: "hp",
                            value: 0,
                            type: PacketValueSerialType.UInt
                        }
                    ]);
                } else {
                    //push back half dist
                    const pushVec = this.transform.position.sub(player.transform.position).normalized;
                    this.transform.position = this.transform.position.add(pushVec.scale(dist/2));
                }
            }
        }
    }

    LateUpdate(): void {
        if (!this.isOwner) return;

        if (!this.team && GameManager.Singleton?.getGameState() === GameState.InGame) {
            return;
        }

        this.engine.camera = Vector2.lerp(this.engine.camera, this.transform.position, Time.deltaTime * 16);

        if (Vector2.distance(this.engine.camera, this.transform.position) < 1) this.engine.camera = this.transform.position.clone();
        
        // this.engine.sendUpdateObjectPacket(this.gameObject, {
        //     "PLAYER": [
        //         {
        //             key: "team",
        //             value: this.team || "",
        //             type: PacketValueSerialType.String
        //         },
        //         {
        //             key: "isDead",
        //             value: this.isDead,
        //             type: PacketValueSerialType.Bool
        //         },
        //         {
        //             key: "hp",
        //             value: this.hp,
        //             type: PacketValueSerialType.UInt
        //         },
        //         {
        //             key: "maxHp",
        //             value: this.maxHp,
        //             type: PacketValueSerialType.UInt
        //         },
        //         {
        //             key: "size",
        //             value: this.size,
        //             type: PacketValueSerialType.Byte
        //         },
        //         {
        //             key: "isCloaked",
        //             value: this.isCloaked,
        //             type: PacketValueSerialType.Bool
        //         },
        //         {
        //             key: "damageReductionModifier",
        //             value: this.damageReductionModifier,
        //             type: PacketValueSerialType.Float
        //         },
        //         {
        //             key: "isMarked",
        //             value: this.isMarked,
        //             type: PacketValueSerialType.Bool
        //         },
        //         {
        //             key: "isDisabled",
        //             value: this.isDisabled,
        //             type: PacketValueSerialType.Bool
        //         },
        //     ]
        // });
    }

    Draw(): void {

        if (GameManager.Singleton?.getGameState() !== GameState.InGame) return;

        if (!this.team) {
            //in game with no team (spectator)
            if (this.isOwner) {
                const spectating = this.engine.GetGameObjectsBehaviors<Player>(Player).filter(v => v.id !== this.id)[this.spectatingPlayerIndex];
                this.engine.drawTexts(
                    [`Spectating: ${spectating.name}`],
                    new Vector2(this.engine.width/2, this.engine.height - 64),
                    {
                        align: 'center',
                        baseline: 'bottom',
                        color: this.engine.viewport.fColor
                    }
                );
            }
            return;
        }

        if (this.isOwner) {
            this.ctx.strokeStyle = '#ffff8033';
            this.ctx.lineWidth = 1;
            const gridSize = toPixels(MetersToUnits(1000));
            const modpos = this.engine.camera.clone();
            modpos.x %= gridSize;
            modpos.y %= gridSize;

            for (let x = 0; x <= this.engine.width - this.engine.width%gridSize + gridSize; x += gridSize) {
                this.engine.LineScreen(new Vector2(x - modpos.x, 0), new Vector2(x - modpos.x, this.engine.height));
            }

            for (let y = 0; y <= this.engine.height - this.engine.height%gridSize + gridSize; y += gridSize) {
                this.engine.LineScreen(new Vector2(0, y - modpos.y), new Vector2(this.engine.width, y - modpos.y));
            }
        }

        let plColor;

        if (this.engine.clientPlayer?.id === this.id) {
            plColor = '#55ffff';
        } else if (this.engine.clientPlayer?.team) {
            plColor = this.engine.clientPlayer.team === this.team ? '#55ff55' : '#ff5555';
        } else {
            plColor = this.team === 'red' ? '#ff5555' : this.team === 'blue' ?  '#5555ff' : '#ffffff';
        }

        if (this.trail && this.trail.color.toHex() !== plColor) this.trail.color = Color.fromHex(plColor);

        if (this.isDead) {
            if (this.engine.clientPlayer?.Equals(this)) {
                this.engine.drawTexts([
                    `You are dead`,
                    `Respawning in ${Math.ceil(this.respawnTimer)}`
                ], this.engine.center, {
                    align: 'center',
                    baseline: 'middle',
                    color: this.engine.viewport.fColor,
                    size: 16
                });
            }
            
            return;
        }

        if (!this.isCloaked || this.isOwner) {
            if (this.class) {
                this.class.Draw(plColor);
            } else {
                this.ctx.strokeStyle = plColor;
                this.ctx.fillStyle = plColor + "55";
                this.ctx.lineWidth = 1;
    
                //draw player
                const p1 = this.transform.position.add(this.transform.forward.scale(this.size));
                const p2 = this.transform.position.add(this.transform.right.rotate(Math.PI/4).scale(this.size));
                const p3 = this.transform.position.add(this.transform.back.scale(this.size/2));
                const p4 = this.transform.position.add(this.transform.left.rotate(-Math.PI/4).scale(this.size));
    
                this.engine.fillShape(p1, p2, p3, p4);
                this.engine.strokeShape(p1, p2, p3, p4);
                //
            }
        }

        

        //debuffs
        if (this.isDisabled && this.timeDisabled) {
            this.ctx.fillStyle = '#ffff5555';
            this.engine.FillRectFromCenter(this.transform.position.add(Vector2.down.scale(this.size + 18)), new Vector2(50, 4));
            this.ctx.fillStyle = '#ffff55';
            this.engine.FillRectFromCenter(this.transform.position.add(Vector2.down.scale(this.size + 18)), new Vector2(50 * clamp01(1 - (Time.time - this.timeDisabled)/5), 4));
        }
        //

        if (this.isOwner) {
            //status bars
            this.ctx.fillStyle = '#ff555555';
            this.ctx.fillRect(8, this.engine.height - 38, 192, 16);
            this.ctx.fillStyle = '#ff5555';
            this.ctx.fillRect(8, this.engine.height - 38, 192  * (this.hp/this.maxHp), 16);

            this.engine.drawTexts(
                [`${Math.floor(this.hp).toLocaleString()}/${this.maxHp.toLocaleString()}`],
                new Vector2(8 + 192/2, this.engine.height - 30),
                {
                    align: 'center',
                    baseline: 'middle',
                    color: 'black'
                }
            );

            this.ctx.fillStyle = '#55ffff55';
            this.ctx.fillRect(8, this.engine.height - 18, 192, 16);
            this.ctx.fillStyle = this.energyCooldown > 0 ? '#ffff55' : '#55ffff';
            this.ctx.fillRect(8, this.engine.height - 18, 192 * (this.energy/this.maxEnergy), 16);

            this.engine.drawTexts(
                [`${Math.floor(this.energy).toLocaleString()}/${this.maxEnergy.toLocaleString()}`],
                new Vector2(8 + 192/2, this.engine.height - 10),
                {
                    align: 'center',
                    baseline: 'middle',
                    color: 'black'
                }
            );

            if (this.class) {
                GameManager.Singleton?.DrawUiIcon(this.class.type, new Vector2(18, this.engine.height - 32-8-18));
            }
            //


            //class abilities overlay F1
            if (Input.GetKey(KeyCode.F1)) {
                this.class?.DrawAbilityOverlay();
            }
            //
        }

        if (!this.isOwner && !this.isCloaked) {
            //health bar over other players
            this.ctx.fillStyle = plColor + '55';
            this.engine.FillRect(this.transform.position.sub(new Vector2(25, 16)), new Vector2(50, 4));
            this.ctx.fillStyle = plColor;
            this.engine.FillRect(this.transform.position.sub(new Vector2(25, 16)), new Vector2(50 * (this.hp/this.maxHp), 4));
        }

        if (!this.isOwner && this.engine.clientPlayer?.team === this.team) {
            this.engine.drawTextsWorldPosition([
                `${this.name}`
            ], this.transform.position.add(Vector2.up.scale(this.size + 12)), {
                align: "center",
                baseline: "bottom"
            });
        }

        if (!this.isOwner) {
            if (this.engine.clientPlayer?.team === this.team || this.isMarked) {
                //markers to other players
                this.engine.drawUIMarker(this.transform.position, plColor, this.engine.clientPlayer?.velocity.magnitude ?? 0);
            }
        }
    }

    DrawGizmos(): void {
        this.engine.drawTextsWorldPosition(
            [
                `${this.gameObject.id}`,
                `${this.transform.position.x}, ${this.transform.position.y}`,
                `V: ${Math.floor(UnitsToMeters(toUnits(this.velocity.magnitude)))} m/s`,
                `A: ${Math.floor(UnitsToMeters(toUnits(this.acceleration.magnitude)))} (m/s)/s`,
                `Class: ${this.class ? PlayerClassType[this.class.type] : 'None'}`,
                `Team: ${this.team}`,
                `Dmg Reduction: ${this.damageReductionModifier*100}%`
            ],
            this.transform.position.add(Vector2.one.scale(this.size+2)),
            {
                color: this.engine.viewport.fColor
            }
        );
    }
}